import type { LearnForgeSDK, User } from '../index';
import { isSDKMessage, makeEvent, makeRequest, type SDKMessage } from '../protocol';

const STANDALONE_USER: User = { id: 'standalone', displayName: 'Standalone Player' };
const REQUEST_TIMEOUT_MS = 10_000;

export function createSDKClient(): LearnForgeSDK {
  const isEmbedded = window.parent !== window;
  if (!isEmbedded) return standaloneClient();

  // The platform host injects `?parent=<its origin>` into the iframe `src`
  // so the game knows whom to talk to. Without it we cannot safely use
  // `postMessage` (broadcasting with '*' targetOrigin would leak user
  // identity / score payloads to any in-page frame), so we degrade to
  // the standalone stub and surface a loud console error for the developer.
  const parentOrigin = readParentOrigin();
  if (parentOrigin === null) {
    console.error(
      '[learnforge/sdk] embedded but no `?parent=<origin>` query param on the game URL. ' +
        'Falling back to standalone — the platform host must inject `?parent` so postMessage ' +
        'can be locked to that origin.',
    );
    return standaloneClient();
  }

  return embeddedClient(parentOrigin);
}

function readParentOrigin(): string | null {
  const raw = new URLSearchParams(window.location.search).get('parent');
  if (raw === null) return null;
  try {
    return new URL(raw).origin;
  } catch {
    return null;
  }
}

interface PendingHandler {
  resolve: (v: unknown) => void;
  reject: (e: Error) => void;
  timeoutId: ReturnType<typeof setTimeout>;
}

function embeddedClient(parentOrigin: string): LearnForgeSDK {
  const pending = new Map<string, PendingHandler>();

  window.addEventListener('message', (e: MessageEvent) => {
    // Defence in depth: only accept responses from the exact frame and
    // origin we handshook with. Any other sender (sibling iframe, injected
    // script, attacker frame) is silently ignored.
    if (e.source !== window.parent) return;
    if (e.origin !== parentOrigin) return;
    if (!isSDKMessage(e.data)) return;
    if (e.data.kind !== 'response') return;
    const handler = pending.get(e.data.id);
    if (!handler) return;
    clearTimeout(handler.timeoutId);
    pending.delete(e.data.id);
    if (e.data.ok) handler.resolve(e.data.result);
    else handler.reject(new Error(e.data.error));
  });

  // If the game is unloading (navigation, tab close), surface a rejection on
  // every in-flight call so promise consumers see a real error instead of
  // a pending-forever promise.
  window.addEventListener('pagehide', () => {
    for (const handler of pending.values()) {
      clearTimeout(handler.timeoutId);
      handler.reject(new Error('[learnforge/sdk] game unloading'));
    }
    pending.clear();
  });

  function call<T>(method: 'getUser' | 'reportScore', args: unknown[]): Promise<T> {
    const msg = makeRequest(method, args) as Extract<SDKMessage, { kind: 'request' }>;
    return new Promise<T>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        if (pending.delete(msg.id)) {
          reject(new Error(`[learnforge/sdk] ${method} timed out after ${REQUEST_TIMEOUT_MS}ms`));
        }
      }, REQUEST_TIMEOUT_MS);
      pending.set(msg.id, {
        resolve: (v) => resolve(v as T),
        reject,
        timeoutId,
      });
      window.parent.postMessage(msg, parentOrigin);
    });
  }

  return {
    getUser: () => call<User>('getUser', []),
    reportScore: (gameId, payload) =>
      call<void>('reportScore', [gameId, payload]).then(() => undefined),
    requestExit: () => {
      const msg: SDKMessage = makeEvent('exit');
      window.parent.postMessage(msg, parentOrigin);
    },
  };
}

function standaloneClient(): LearnForgeSDK {
  return {
    getUser: () => Promise.resolve(STANDALONE_USER),
    reportScore: () => {
      // No network in standalone mode. The game keeps local progress via
      // @learnforge/sdk/progress; the platform round-trip is the only thing
      // we'd report to, and it isn't there.
      return Promise.resolve();
    },
    requestExit: () => {
      window.location.reload();
    },
  };
}
